import React from 'react';
import styled from 'styled-components';
import useAxios from 'axios-hooks';
import { Button } from '@mui/material';
import { toast } from 'react-toastify';
import { StatusCodes } from 'http-status-codes';
import { PhotoCamera as PhotoCameraIcon } from '@mui/icons-material';
import { changeModals, NModals } from '../modals';
import { ModalLayout } from '../layout';
import classes from '../../../utils/classes';
import {
	API_ROUTES,
	CLOUDINARY_API_ROUTES,
} from '../../../routes';
import { getUniqueId } from '../../../scripts/unique-id-manager';
import { Auth } from '../../../utils/auth';
import checkUpload from '../../../utils/check-upload';
import buildApiRoute from '../../../utils/build-api-route';
import {
	useDispatch,
	useSelector,
} from '../../../store/store';
import { fetchUser } from '../../../store/slices/user';

const defaultImage = '1.png';
const images: NModalChangePicture.IImage[] = [
	{
		name: 'Silly cat',
		fileName: '1.png',
	},
	{
		name: 'Silly twat',
		fileName: '2.png',
	},
	{
		name: 'Silly brat',
		fileName: '3.png',
	},
	{
		name: '4',
		fileName: '4.png',
	},
	{
		name: '5',
		fileName: '5.png',
	},
	{
		name: '6',
		fileName: '6.png',
	},
];

const name = 'ModalChangePicture';
const Modal = (
	props: NModalChangePicture.IProps,
) => {
	const { user, loading } = useSelector(
		(st) => st.user,
	);
	const dispatch = useDispatch();

	const [selected, setSelected] =
		React.useState<NModalChangePicture.IImage | null>(
			null,
		);
	const [currentImage, setCurrentImage] =
		React.useState<string | null>(null);
	const [custom, setCustom] = React.useState<
		string | null
	>(null);

	const [, userChangePicture] = useAxios(
		{
			method: 'POST',
			url: API_ROUTES.changePicture,
		},
		{ manual: true, autoCancel: true },
	);

	const changePicture = (
		image: NModalChangePicture.IImage,
	) => {
		setSelected(image);
	};

	const uploadCustom = () => {
		window.dispatchEvent(
			changeModals({
				ModalUploadPicture: { open: true },
			}),
		);
	};

	const handleSave = async () => {
		const res = await userChangePicture({
			headers: {
				Authorization: `Bearer ${Auth.getToken()}`,
				uniqueId: getUniqueId(),
			},
			data: {
				picture: selected?.fileName,
			},
		});
		if (
			res?.data?.statusCode === StatusCodes.OK
		) {
			window.dispatchEvent(
				changeModals({ [name]: null }),
			);
			toast('Picture changed', {
				type: 'success',
			});
			dispatch(fetchUser());
		} else {
			toast('Failed to change picture', {
				type: 'error',
			});
		}
	};

	const handleStates = async () => {
		if (user?.picture) {
			setSelected(
				images.find(
					(f) => f.fileName === user?.picture,
				) || null,
			);
			setCurrentImage(user?.picture);
			setCustom(null);
		} else if (user?.picture_custom) {
			const imagePath = buildApiRoute(
				CLOUDINARY_API_ROUTES.getUpload,
				{
					folder: `pictures`,
					name: `${user?._id}_${user?.picture_custom}.webp`,
				},
			);
			const uploadedImage = await checkUpload(
				imagePath,
			);

			if (uploadedImage) {
				setSelected(null);
				setCurrentImage(null);
				setCustom(imagePath);
			} else {
				setSelected(
					images.find(
						(f) => f.fileName === defaultImage,
					) || null,
				);
				setCurrentImage(defaultImage);
				setCustom(null);
			}
		} else {
			setSelected(
				images.find(
					(f) => f.fileName === defaultImage,
				) || null,
			);
			setCurrentImage(defaultImage);
			setCustom(null);
		}
	};

	React.useEffect(() => {
		handleStates();
	}, []);

	return (
		<ModalLayout
			showHeader
			title="Change picture"
			{...props}
			name={name}
		>
			<ModalChangePictureStyle>
				<div className="content">
					{images.map((m, i) => (
						<div
							key={i}
							className={classes(
								'image-box',
								selected?.fileName === m.fileName
									? 'selected'
									: '',
							)}
							onClick={() => changePicture(m)}
						>
							<div
								className="image"
								style={{
									backgroundImage: `url(/img/pictures/${m.fileName})`,
								}}
							/>
						</div>
					))}
					<div
						className={classes(
							'image-box',
							'image-box-custom',
							custom && !loading
								? 'uploaded'
								: '',
						)}
						onClick={() => uploadCustom()}
					>
						<div
							className={classes(
								'choose-image',
								custom
									? 'choose-image-uploaded'
									: '',
							)}
						>
							<PhotoCameraIcon />
							<span>
								{custom && !loading
									? 'Change'
									: 'Upload'}
							</span>
						</div>
						{custom ? (
							<div
								className="image"
								style={{
									backgroundImage: `url(${custom})`,
								}}
							/>
						) : null}
					</div>
				</div>
				<div className="modal-footer">
					<Button
						disabled={
							loading ||
							selected?.fileName ===
								currentImage ||
							!selected
						}
						onClick={handleSave}
					>
						Save
					</Button>
				</div>
			</ModalChangePictureStyle>
		</ModalLayout>
	);
};

export { name, Modal };

export namespace NModalChangePicture {
	// eslint-disable-next-line @typescript-eslint/no-empty-interface
	export interface IProps
		extends NModals.IDefaultProps {
		//
	}

	export interface IImage {
		name: string;
		fileName: string;
	}
}

const ModalChangePictureStyle = styled.div`
	max-width: 500px;
	> h2 {
		color: ${({ theme }) =>
			theme?.palette?.primary?.main};
	}
	.content {
		display: flex;
		flex-wrap: wrap;
		row-gap: 10px;
		column-gap: 10px;
		.image-box {
			display: flex;
			justify-content: center;
			align-items: center;
			cursor: pointer;
			position: relative;
			border: 3px solid transparent;
			border-radius: 50%;
			width: 62px;
			height: 62px;
			box-sizing: border-box;
			.choose-image {
				display: flex;
				justify-content: center;
				align-items: center;
				flex-direction: column;
				font-size: 12px;
				width: 100%;
				height: 100%;
				background-color: ${({ theme }) =>
					theme?.palette?.background?.default};
				border-radius: 50%;
			}
			&.selected {
				border-color: ${({ theme }) =>
					theme?.palette?.primary?.light};
			}
			.image {
				width: 100%;
				height: 100%;
				background-position: center;
				background-repeat: no-repeat;
				background-size: cover;
				border-radius: 50%;
			}
		}
		.image-box-custom {
			position: relative;
			&.uploaded {
				.choose-image {
					opacity: 0;
					&:hover {
						opacity: 0.8;
					}
				}
			}
			.choose-image {
				position: absolute;
				top: 0;
				left: 0;
			}
		}
	}

	.modal-footer {
		display: flex;
		justify-content: flex-end;
		margin-top: 15px;
	}
`;
